'use client';

/**
 * FunilTabs — Section "Como funciona" da home / (Figma 81:70).
 *
 * Comportamento (per spec PL 2026-05-15):
 *   - 3 tabs: "Escolha seu Pass" | "Ative seu perfil" | "Aproveite a Brisa"
 *   - Indicador amarelo desliza no ativo (Tabs primitivo já trata)
 *   - Primary CTAs scroll para #waitlist
 *   - Step 1 tem tertiary-link "Conhecer os planos →" → /clubes
 *   - Microinteração: cross-fade + leve translate ao trocar de tab
 *
 * Honra catálogo (storybook + Figma):
 *   - Tabs primitivo (h-1 indicator amarelo)
 *   - PassCardWithoutCta (2 lado a lado no Step 1)
 *   - IdentityCardBrisaV2 + QR slot ao lado (Step 2)
 *   - EventCardHorizontal (3 stacked no Step 3)
 *   - Button variant primary-medium pros CTAs do funil, terciary-link pro Step 1
 */
import * as React from 'react';
import Link from 'next/link';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Ticket, ArrowRight, Lock, UserPlus } from 'lucide-react';

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@brisa/ui/components/ui/tabs';
import { Button } from '@brisa/ui/components/ui/button';
import { PassCardWithoutCta } from '@brisa/ui/components/facelift/pass-card/PassCardWithoutCta';
import { IdentityCardBrisaV2 } from '@brisa/ui/components/facelift/identity-card/IdentityCardBrisaV2';
import { EventCardHorizontal } from '@brisa/ui/components/facelift/event-card/EventCardHorizontal';
import type { PassCatalogItem } from '@brisa/ui/components/facelift/pass-card/PassCardWithoutCta';
import type { EventCardEvent } from '@brisa/ui/components/facelift/event-card/EventCardHorizontal';
import { cn } from '@brisa/ui/lib/utils';

export interface FunilTabsCopy {
  sectionTag: string;
  tab1Label: string;
  tab2Label: string;
  tab3Label: string;
  step1Heading: string;
  step1Cta: string;
  step1Tertiary: string;
  step2Heading: string;
  step2Cta: string;
  step2Helper: string;
  step3Heading: string;
  step3Cta: string;
}

export interface FunilTabsProps {
  copy: FunilTabsCopy;
  plans: PassCatalogItem[];
  events: EventCardEvent[];
  locale?: 'pt-BR' | 'es-ES';
  scrollTarget?: string;
  step1TertiaryHref?: string;
  surface?: string;
}

const panelTransition = { duration: 0.5, ease: 'easeOut' as const };

/**
 * QR placeholder ilustrativo — SVG pattern simulando código QR.
 * Não é um QR real (decorativo); fica parcialmente atrás do IdentityCard
 * no Step 2 do Funil, per Figma 148:154.
 */
/**
 * QR ilustrativo (não-funcional) — fica parcialmente atrás do IdentityCard
 * no Step 2 do Funil per Figma 148:154. Cantos com finder patterns + padrão
 * pseudo-aleatório estável.
 */

function PanelMotion({
  children,
  panelKey,
}: {
  children: React.ReactNode;
  panelKey: string;
}) {
  return (
    <motion.div
      key={panelKey}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={panelTransition}
      // Altura mínima consistente entre abas pra evitar pulo no layout (per PL).
      className="grid min-h-[420px] w-full gap-10 md:grid-cols-2 md:items-center"
    >
      {children}
    </motion.div>
  );
}

export function FunilTabs({
  copy,
  plans,
  events,
  locale = 'pt-BR',
  scrollTarget = 'waitlist',
  step1TertiaryHref = '/clubes',
  surface = 'home_funil',
}: FunilTabsProps) {
  const [value, setValue] = React.useState('step1');
  // Trigger por entrada na viewport — animações disparam ao usuário scrollar
  // até a seção (em vez de no mount inicial, que costuma estar acima do fold).
  // `once: true` mantém o painel renderizado depois do primeiro trigger.
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const scrollToWaitlist = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const el = document.getElementById(scrollTarget);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [scrollTarget],
  );

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-12 lg:py-20"
      aria-label="Como funciona"
    >
      <div className="mx-auto flex w-full max-w-[1024px] flex-col items-center px-6 sm:px-10">
        {/* Section tag (pill cinza pequena) */}
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full px-4 py-2',
            'bg-[hsl(var(--text-08))] font-body text-xs font-semibold text-[hsl(var(--text-64))]',
          )}
          data-slot="section-tag"
        >
          {copy.sectionTag}
        </span>

        <Tabs
          variant="facelift-underline"
          value={value}
          onValueChange={setValue}
          className="mt-8 flex w-full flex-col items-center"
          data-slot="funil-tabs"
        >
          <TabsList data-slot="funil-tabs-list">
            <TabsTrigger value="step1" data-slot="tab-step1">
              {copy.tab1Label}
            </TabsTrigger>
            <TabsTrigger value="step2" data-slot="tab-step2">
              {copy.tab2Label}
            </TabsTrigger>
            <TabsTrigger value="step3" data-slot="tab-step3">
              {copy.tab3Label}
            </TabsTrigger>
          </TabsList>

          {/* Demo-product area com cross-fade entre painéis.
              min-h reserva o slot enquanto a seção ainda não entrou no viewport
              (isInView=false) — assim não tem pulo de layout no scroll. */}
          <div className="relative mt-14 min-h-[420px] w-full" data-slot="demo-product">
            <AnimatePresence mode="wait" initial={false}>
              {isInView && value === 'step1' && (
                <TabsContent value="step1" forceMount asChild>
                  <PanelMotion panelKey="step1">
                    <div className="flex flex-col items-center gap-8 text-center lg:!items-start lg:!text-left">
                      <h3 className="max-w-[25ch] font-body text-2xl font-medium leading-snug text-[hsl(var(--text-56))] text-center lg:!text-left">
                        {copy.step1Heading}
                      </h3>
                      <div className="flex flex-wrap items-center gap-6">
                        <Button
                          type="button"
                          onClick={scrollToWaitlist}
                          variant="primary-medium"
                          size="md"
                          data-event="landing-cta"
                          data-surface={`${surface}_step1`}
                          data-destination="waitlist"
                        >
                          {copy.step1Cta}
                          <Ticket size={16} aria-hidden="true" />
                        </Button>
                        <Button variant="terciary-link" size="md" asChild>
                          <Link
                            href={step1TertiaryHref}
                            data-event="landing-cta"
                            data-surface={`${surface}_step1`}
                            data-destination="clubes"
                          >
                            {copy.step1Tertiary}
                            <ArrowRight size={14} aria-hidden="true" />
                          </Link>
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-stretch gap-4">
                      {plans.slice(0, 2).map((plan, i) => (
                        <motion.div
                          key={plan.key}
                          initial={{ opacity: 0, y: 32 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            delay: 0.3 + i * 0.22,
                            duration: 0.55,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                          className="min-w-0 flex-1"
                        >
                          <PassCardWithoutCta
                            plan={plan}
                            className="h-full w-full"
                          />
                        </motion.div>
                      ))}
                    </div>
                  </PanelMotion>
                </TabsContent>
              )}

              {isInView && value === 'step2' && (
                <TabsContent value="step2" forceMount asChild>
                  <PanelMotion panelKey="step2">
                    <div className="flex flex-col items-center gap-8 text-center lg:!items-start lg:!text-left">
                      <h3 className="max-w-[25ch] font-body text-2xl font-medium leading-snug text-[hsl(var(--text-56))] text-center lg:!text-left">
                        {copy.step2Heading}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4">
                        <Button
                          type="button"
                          onClick={scrollToWaitlist}
                          variant="primary-medium"
                          size="md"
                          data-event="landing-cta"
                          data-surface={`${surface}_step2`}
                          data-destination="waitlist"
                        >
                          {copy.step2Cta}
                          <Lock size={16} aria-hidden="true" />
                        </Button>
                        <span className="font-body text-xs font-semibold text-[hsl(var(--text-56))]">
                          {copy.step2Helper}
                        </span>
                      </div>
                    </div>

                    <div className="relative flex items-center justify-center">
                      {/* QR ilustrativo oficial (asset entregue pelo PL 2026-05-15) —
                          fica parcialmente atrás do IdentityCard per Figma 148:154.
                          Microinteração: começa atrás do card (x:120) e desliza pra
                          esquerda emergindo (delay 0.45s após o card aparecer). */}
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <motion.img
                        src="/landing/qr-code.svg"
                        alt=""
                        aria-hidden="true"
                        initial={{ x: 120 }}
                        animate={{ x: 0 }}
                        transition={{
                          delay: 0.45,
                          duration: 0.75,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        className="relative z-0 hidden h-[244px] w-[216px] md:block"
                        data-slot="qr-illustration"
                      />
                      <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.15,
                          duration: 0.5,
                          ease: 'easeOut' as const,
                        }}
                        className="relative z-10 md:-ml-24"
                      >
                        <IdentityCardBrisaV2
                          member={{
                            name: 'Rafael Silva',
                            avatar_url: 'https://i.pravatar.cc/160?img=12',
                          }}
                          stats={[
                            { label: 'Visitas', value: 10 },
                            { label: 'Eventos', value: 5 },
                            { label: 'Clubs', value: 2 },
                          ]}
                          onEdit={() => undefined}
                        />
                      </motion.div>
                    </div>
                  </PanelMotion>
                </TabsContent>
              )}

              {isInView && value === 'step3' && (
                <TabsContent value="step3" forceMount asChild>
                  <PanelMotion panelKey="step3">
                    <div className="flex flex-col items-center gap-8 text-center lg:!items-start lg:!text-left">
                      <h3 className="max-w-[25ch] font-body text-2xl font-medium leading-snug text-[hsl(var(--text-56))] text-center lg:!text-left">
                        {copy.step3Heading}
                      </h3>
                      <Button
                        type="button"
                        onClick={scrollToWaitlist}
                        variant="primary-medium"
                        size="md"
                        data-event="landing-cta"
                        data-surface={`${surface}_step3`}
                        data-destination="waitlist"
                      >
                        {copy.step3Cta}
                        <UserPlus size={16} aria-hidden="true" />
                      </Button>
                    </div>

                    <div className="flex w-full flex-col">
                      {/* Microinteração: cards arrivam "empilhados" no slot de baixo
                          (cada um translateY positivo = nº de slots abaixo do natural)
                          e sobem pra revelar a pilha em cascata. */}
                      <ul className="flex w-full flex-col gap-3" data-slot="events-list">
                        {events.map((event, i) => (
                          <motion.li
                            key={event.id}
                            initial={{ y: `${(events.length - 1 - i) * 100}%` }}
                            animate={{ y: 0 }}
                            transition={{
                              delay: 0.3 + i * 0.18,
                              duration: 0.55,
                              ease: [0.22, 1, 0.36, 1],
                            }}
                          >
                            <EventCardHorizontal event={event} locale={locale} />
                          </motion.li>
                        ))}
                      </ul>
                      {/* Base ilustrativa — assinala "tem mais eventos atrás" per PL 2026-05-15.
                          Asset oficial (baseUltimoCard.png, 418×16 — fatia decorativa). */}
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src="/landing/base-ultimo-card.png"
                        alt=""
                        aria-hidden="true"
                        data-slot="events-ghost-base"
                        className="pointer-events-none mx-auto block w-[92%] select-none"
                      />
                    </div>
                  </PanelMotion>
                </TabsContent>
              )}
            </AnimatePresence>
          </div>
        </Tabs>
      </div>
    </section>
  );
}
